import { useEffect, useRef, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import AppShell, { SectionCard, StatCard } from "../components/AppShell";
import Gate from "../components/Gate";
import { api, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "../components/ui/dialog";
import { toast } from "sonner";
import { Plus, CheckCircle, Circle, ArrowRight, ArrowLeft, CalendarBlank, PaperPlaneTilt, UploadSimple, LinkSimple, ClockClockwise, FileText } from "@phosphor-icons/react";
import { DateInput } from "../components/ui/date-input";

const STAGE_LABELS = {
  pre_joining: "Pre-joining",
  day_1: "Day 1",
  week_1: "Week 1",
  month_1: "Month 1",
  probation: "Probation",
  custom: "Custom",
};

const ASSIGNEE_COLORS = {
  hr: "bg-blue-50 border-blue-200 text-blue-700",
  it: "bg-violet-50 border-violet-200 text-violet-700",
  admin: "bg-amber-50 border-amber-200 text-amber-700",
  manager: "bg-emerald-50 border-emerald-200 text-emerald-700",
  employee: "bg-zinc-50 border-zinc-200 text-zinc-700",
  finance: "bg-rose-50 border-rose-200 text-rose-700",
};

export default function Onboarding() {
  return (
    <AppShell title="Onboarding">
      <Gate module="onboarding">
        <OnboardingInner />
      </Gate>
    </AppShell>
  );
}

function OnboardingInner() {
  const [rows, setRows] = useState([]);
  const [allRows, setAllRows] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [statusF, setStatusF] = useState("active");
  const [employees, setEmployees] = useState([]);
  const [newOnboarding, setNewOnboarding] = useState({ employee_id: "", template_id: "", date_of_joining: "" });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [startError, setStartError] = useState("");
  const [startBusy, setStartBusy] = useState(false);
  const startReady = !!(newOnboarding.employee_id && newOnboarding.template_id && newOnboarding.date_of_joining);
  const [analytics, setAnalytics] = useState(null);
  const [resignations, setResignations] = useState([]);

  const load = async () => {
    const [o, all, t, e] = await Promise.all([
      api.get(`/onboarding${statusF !== "all" ? `?status=${statusF}` : ""}`),
      api.get("/onboarding"),
      api.get("/onboarding/templates"),
      api.get("/employees"),
    ]);
    setRows(o.data); setAllRows(all.data); setTemplates(t.data); setEmployees(e.data);
    // Best-effort: analytics + resignations (HR-only — silently skip if 403)
    try {
      const [a, r] = await Promise.all([
        api.get("/onboarding/analytics/overview").catch(() => ({ data: null })),
        api.get("/offboarding/resignations?status=pending").catch(() => ({ data: [] })),
      ]);
      setAnalytics(a.data); setResignations(r.data || []);
    } catch { /* not HR — ignore */ }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusF]);

  const start = async () => {
    setStartError("");
    const missing = [];
    if (!newOnboarding.employee_id) missing.push("Employee");
    if (!newOnboarding.template_id) missing.push("Template");
    if (!newOnboarding.date_of_joining) missing.push("Date of joining");
    if (missing.length) {
      setStartError(`Please fill: ${missing.join(", ")}`);
      toast.error(`Missing: ${missing.join(", ")}`);
      return;
    }
    setStartBusy(true);
    try {
      await api.post("/onboarding", newOnboarding);
      toast.success("Onboarding started");
      setDialogOpen(false);
      setNewOnboarding({ employee_id: "", template_id: "", date_of_joining: "" });
      setStartError("");
      await load();
    } catch (e) {
      const msg = formatApiError(e?.response?.data?.detail) || "Failed to start";
      setStartError(msg);
      toast.error(msg);
    } finally {
      setStartBusy(false);
    }
  };

  const counts = {
    active: allRows.filter(r => r.status === "active").length,
    completed: allRows.filter(r => r.status === "completed").length,
    total: allRows.length,
  };

  return (
    <>
      <div className="grid grid-cols-3 gap-4 mb-6">
        <StatCard label="Active onboardings" value={counts.active} testid="stat-active"/>
        <StatCard label="Completed" value={counts.completed} testid="stat-completed"/>
        <StatCard label="Templates available" value={templates.length} testid="stat-templates"/>
      </div>

      {analytics && (
        <SectionCard
          title="Onboarding analytics"
          subtitle={analytics.total_sample_size > 0
            ? "Performance signals across your active and recent onboardings."
            : "Performance signals will appear once you complete your first onboarding."}
          testid="onboarding-analytics"
        >
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="bg-white border border-zinc-200 rounded-md p-4">
              <div className="text-[11px] uppercase tracking-wider text-zinc-500">Avg TAT</div>
              <div className="text-2xl font-display mt-1.5">{analytics.avg_tat_days}<span className="text-sm text-zinc-500"> days</span></div>
            </div>
            <div className="bg-white border border-zinc-200 rounded-md p-4">
              <div className="text-[11px] uppercase tracking-wider text-zinc-500">On-time completion</div>
              <div className="text-2xl font-display mt-1.5">{analytics.on_time_completion_pct}<span className="text-sm text-zinc-500">%</span></div>
            </div>
            <div className="bg-white border border-zinc-200 rounded-md p-4">
              <div className="text-[11px] uppercase tracking-wider text-zinc-500">Completed this month</div>
              <div className="text-2xl font-display mt-1.5">{analytics.completed_this_month}</div>
            </div>
            <div className="bg-white border border-zinc-200 rounded-md p-4">
              <div className="text-[11px] uppercase tracking-wider text-zinc-500">Pending doc reviews</div>
              <div className="text-2xl font-display mt-1.5">{analytics.pending_doc_verifications}</div>
            </div>
          </div>
          {analytics.top_stuck_tasks?.length > 0 && (
            <div className="mt-5">
              <div className="text-xs uppercase tracking-wider text-zinc-500 mb-2">Top stuck tasks (overdue)</div>
              <div className="space-y-1.5">
                {analytics.top_stuck_tasks.map((t, i) => (
                  <div key={i} className="flex items-center justify-between bg-amber-50 border border-amber-200 rounded-md px-3 py-2 text-sm" data-testid={`stuck-task-${i}`}>
                    <span className="truncate">{t.title}</span>
                    <Badge variant="outline" className="text-[10px] bg-white">{t.count} overdue</Badge>
                  </div>
                ))}
              </div>
            </div>
          )}
        </SectionCard>
      )}

      {resignations.length > 0 && (
        <SectionCard
          title={`Resignation requests · ${resignations.length} pending`}
          subtitle="Employee-initiated exits awaiting approval. Each fires through the workflow engine."
          testid="resignation-inbox"
        >
          <div className="space-y-2">
            {resignations.map((r) => (
              <div key={r.id} className="flex items-center justify-between bg-white border border-zinc-200 rounded-md px-4 py-3" data-testid={`resignation-row-${r.id}`}>
                <div>
                  <div className="font-medium text-sm">{r.employee_name}</div>
                  <div className="text-xs text-zinc-500 mt-0.5">LWD {r.last_working_day} · Notice {r.notice_period_days}d · Reason: {r.reason}{r.reason_details ? ` — "${r.reason_details}"` : ""}</div>
                </div>
                {r.approval_request_id && (
                  <Link to="/app/approvals" className="text-xs font-medium text-blue-600 hover:underline">Open in approvals →</Link>
                )}
              </div>
            ))}
          </div>
        </SectionCard>
      )}

      <SectionCard
        title="All onboardings"
        subtitle="Track new hire journeys across stages — pre-joining, day 1, week 1, month 1, probation."
        testid="section-onboardings"
        action={
          <div className="flex items-center gap-2">
            <Select value={statusF} onValueChange={setStatusF}>
              <SelectTrigger className="w-40 h-9" data-testid="filter-status"><SelectValue/></SelectTrigger>
              <SelectContent>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
                <SelectItem value="all">All</SelectItem>
              </SelectContent>
            </Select>
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button size="sm" className="gap-1.5" data-testid="start-onboarding-btn"><Plus size={14} weight="bold"/> Start onboarding</Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>Start new hire onboarding</DialogTitle></DialogHeader>
                <div className="space-y-4 py-2">
                  <div>
                    <Label>Employee</Label>
                    <Select value={newOnboarding.employee_id} onValueChange={v=>setNewOnboarding(o=>({...o,employee_id:v}))}>
                      <SelectTrigger className="mt-1" data-testid="new-onb-employee"><SelectValue placeholder="Pick an employee"/></SelectTrigger>
                      <SelectContent>{employees.filter(e=>e.status !== "terminated").map(e=><SelectItem key={e.id} value={e.id}>{e.name} · {e.employee_code}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Template</Label>
                    <Select value={newOnboarding.template_id} onValueChange={v=>setNewOnboarding(o=>({...o,template_id:v}))}>
                      <SelectTrigger className="mt-1" data-testid="new-onb-template"><SelectValue placeholder={templates.length ? "Pick a template" : "No templates yet — create one below"}/></SelectTrigger>
                      <SelectContent>
                        {templates.length === 0 && <div className="px-3 py-2 text-xs text-zinc-500">No templates yet. Close this and use <span className="font-medium">"Onboarding templates"</span> below to create one.</div>}
                        {templates.map(t=><SelectItem key={t.id} value={t.id}>{t.name}{t.is_default?" (default)":""}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Date of joining</Label>
                    <DateInput
                      value={newOnboarding.date_of_joining}
                      onChange={e=>setNewOnboarding(o=>({...o,date_of_joining:e.target.value}))}
                      className="mt-1"
                      data-testid="new-onb-doj"
                      min="2000-01-01"
                    />
                    <div className="text-[11px] text-zinc-500 mt-1">Use the date-picker (calendar icon). Manually typed dates are ignored by the browser.</div>
                  </div>
                </div>
                {startError && (
                  <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2 mt-2" data-testid="start-onb-error">
                    {startError}
                  </div>
                )}
                <DialogFooter>
                  <Button variant="ghost" onClick={()=>setDialogOpen(false)}>Cancel</Button>
                  <Button onClick={start} disabled={!startReady || startBusy} data-testid="new-onb-submit">{startBusy ? "Starting…" : "Start"}</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        }
      >
        {rows.length === 0 && <div className="text-sm text-zinc-500 py-8 text-center">No onboardings match.</div>}
        <div className="divide-y divide-zinc-100">
          {rows.map(ob=>{
            const total = ob.tasks?.length || 0;
            const done = (ob.tasks||[]).filter(t=>t.status==="done"||t.status==="skipped").length;
            const pct = total === 0 ? 0 : Math.round((done/total)*100);
            return (
              <Link key={ob.id} to={`/app/onboarding/${ob.id}`} className="flex items-center gap-4 py-4 hover:bg-zinc-50 px-2 -mx-2 rounded-md" data-testid={`onb-row-${ob.id}`}>
                <div className="flex-1">
                  <div className="font-medium">{ob.employee_name}</div>
                  <div className="text-xs text-zinc-500 mt-0.5">DOJ {ob.date_of_joining} · {ob.template_name}</div>
                </div>
                <div className="w-48">
                  <div className="flex items-center justify-between text-xs text-zinc-600 mb-1"><span>{done}/{total}</span><span>{pct}%</span></div>
                  <div className="h-1.5 bg-zinc-100 rounded-full"><div className="h-full bg-emerald-500 rounded-full" style={{width:`${pct}%`}}/></div>
                </div>
                <Badge variant="outline" className="uppercase text-[10px]">{ob.status}</Badge>
                <ArrowRight size={16} className="text-zinc-400"/>
              </Link>
            );
          })}
        </div>
      </SectionCard>

      <TemplatesSection templates={templates} onChanged={load} />
    </>
  );
}

function TemplatesSection({ templates, onChanged }) {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null); // template doc or null
  return (
    <SectionCard
      title="Onboarding templates"
      subtitle="Tasks across pre-joining, day 1, week 1, month 1, and probation — each with an assignee and due-day offset from joining."
      right={<Button size="sm" variant="outline" className="gap-1.5" onClick={() => { setEditing(null); setOpen(true); }} data-testid="new-template-btn"><Plus size={14}/> New template</Button>}
    >
      {templates.length === 0 && <div className="text-sm text-zinc-500 py-6 text-center">No templates yet. Click <span className="font-medium">"New template"</span> to build one.</div>}
      <div className="divide-y divide-zinc-100">
        {templates.map((t) => (
          <div key={t.id} className="flex items-center justify-between py-3" data-testid={`tpl-row-${t.id}`}>
            <div className="min-w-0">
              <div className="font-medium flex items-center gap-2">
                {t.name}
                {t.is_default && <Badge className="bg-amber-100 text-amber-800 border-amber-200 text-[10px]">Default</Badge>}
                <Badge variant="outline" className="text-[10px]">{(t.tasks||[]).length} tasks</Badge>
              </div>
              {t.description && <div className="text-xs text-zinc-500 mt-0.5 truncate max-w-xl">{t.description}</div>}
            </div>
            <Button size="sm" variant="outline" onClick={() => { setEditing(t); setOpen(true); }} data-testid={`tpl-edit-${t.id}`}>Edit</Button>
          </div>
        ))}
      </div>
      <TemplateEditorDialog
        open={open}
        onClose={() => { setOpen(false); setEditing(null); }}
        initial={editing}
        onSaved={() => { setOpen(false); setEditing(null); onChanged?.(); }}
      />
    </SectionCard>
  );
}

function TemplateEditorDialog({ open, onClose, initial, onSaved }) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [isDefault, setIsDefault] = useState(false);
  const [tasks, setTasks] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setName(initial?.name || "");
      setDescription(initial?.description || "");
      setIsDefault(!!initial?.is_default);
      setTasks(initial?.tasks ? JSON.parse(JSON.stringify(initial.tasks)) : []);
    }
  }, [open, initial]);

  const addTask = () => setTasks((rows) => [...rows, { stage: "day_1", title: "", assignee: "hr", due_days_from_doj: 0 }]);
  const removeTask = (i) => setTasks((rows) => rows.filter((_, idx) => idx !== i));
  const updateTask = (i, k, v) => setTasks((rows) => rows.map((r, idx) => idx === i ? { ...r, [k]: v } : r));

  const save = async () => {
    if (!name.trim()) { toast.error("Name is required"); return; }
    if (tasks.length === 0) { toast.error("Add at least one task"); return; }
    if (tasks.some((t) => !t.title.trim())) { toast.error("Every task needs a title"); return; }
    setBusy(true);
    try {
      const body = {
        name: name.trim(),
        description: description.trim(),
        is_default: isDefault,
        tasks: tasks.map((t) => ({
          stage: t.stage, title: t.title.trim(), assignee: t.assignee,
          due_days_from_doj: Number(t.due_days_from_doj) || 0,
          description: t.description || "",
        })),
      };
      if (initial?.id) {
        await api.put(`/onboarding/templates/${initial.id}`, body);
        toast.success(`Template "${body.name}" updated`);
      } else {
        await api.post("/onboarding/templates", body);
        toast.success(`Template "${body.name}" created`);
      }
      onSaved?.();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Save failed");
    } finally { setBusy(false); }
  };

  const del = async () => {
    if (!initial?.id) return;
    if (!window.confirm(`Delete template "${initial.name}"? Existing onboardings using it will not be affected.`)) return;
    setBusy(true);
    try {
      await api.delete(`/onboarding/templates/${initial.id}`);
      toast.success(`Template deleted`);
      onSaved?.();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Delete failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <DialogContent className="sm:max-w-3xl" data-testid="template-editor">
        <DialogHeader><DialogTitle>{initial ? "Edit template" : "New onboarding template"}</DialogTitle></DialogHeader>
        <div className="space-y-3 max-h-[70vh] overflow-auto pr-1">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Name *</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Engineering — India" className="mt-1" data-testid="tpl-name"/>
            </div>
            <div className="flex items-end">
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={isDefault} onChange={(e) => setIsDefault(e.target.checked)} data-testid="tpl-default"/>
                <span>Default template for this company</span>
              </label>
            </div>
          </div>
          <div>
            <Label>Description</Label>
            <Textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2} placeholder="When to use it" className="mt-1" data-testid="tpl-desc"/>
          </div>
          <div className="flex items-center justify-between mt-3 mb-1">
            <Label>Tasks ({tasks.length})</Label>
            <Button size="sm" variant="outline" onClick={addTask} className="gap-1" data-testid="tpl-add-task"><Plus size={12}/> Add task</Button>
          </div>
          <div className="space-y-2">
            {tasks.map((t, i) => (
              <div key={i} className="grid grid-cols-12 gap-2 items-start bg-zinc-50 border border-zinc-200 rounded p-2" data-testid={`tpl-task-${i}`}>
                <div className="col-span-2">
                  <Select value={t.stage} onValueChange={(v) => updateTask(i, "stage", v)}>
                    <SelectTrigger className="h-9 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>{Object.entries(STAGE_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="col-span-5">
                  <Input value={t.title} onChange={(e) => updateTask(i, "title", e.target.value)} placeholder="Task title" className="h-9 text-sm"/>
                </div>
                <div className="col-span-2">
                  <Select value={t.assignee} onValueChange={(v) => updateTask(i, "assignee", v)}>
                    <SelectTrigger className="h-9 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="hr">HR</SelectItem>
                      <SelectItem value="it">IT</SelectItem>
                      <SelectItem value="admin">Admin</SelectItem>
                      <SelectItem value="manager">Manager</SelectItem>
                      <SelectItem value="employee">Employee</SelectItem>
                      <SelectItem value="finance">Finance</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-2">
                  <Input type="number" value={t.due_days_from_doj} onChange={(e) => updateTask(i, "due_days_from_doj", e.target.value)} className="h-9 text-sm" placeholder="Day offset" title="Days from joining (negative = before DOJ)"/>
                </div>
                <div className="col-span-1 flex justify-end">
                  <Button size="icon" variant="ghost" onClick={() => removeTask(i)} className="h-9 w-9 text-zinc-400 hover:text-red-600">✕</Button>
                </div>
              </div>
            ))}
            {tasks.length === 0 && <div className="text-xs text-zinc-500 text-center py-3">No tasks yet — click "Add task" to start.</div>}
          </div>
        </div>
        <DialogFooter className="gap-2">
          {initial?.id && <Button variant="outline" onClick={del} disabled={busy} className="text-red-600 mr-auto" data-testid="tpl-delete">Delete</Button>}
          <Button variant="ghost" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={save} disabled={busy} data-testid="tpl-save">{busy ? "Saving…" : (initial ? "Save changes" : "Create template")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ----------------- Detail page -----------------
export function OnboardingDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [ob, setOb] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get(`/onboarding/${id}`);
      setOb(data);
    } catch { toast.error("Not found"); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const updateTask = async (task_id, status) => {
    try {
      const { data } = await api.patch(`/onboarding/${id}/task/${task_id}`, { status });
      setOb(data);
      toast.success(status === "done" ? "Task completed" : "Updated");
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  const finish = async (kind) => {
    const msg = kind === "complete"
      ? "Mark this onboarding complete? The employee becomes active."
      : "Cancel this onboarding? Open tasks are closed and the employee stays on record.";
    if (!window.confirm(msg)) return;
    try {
      await api.post(`/onboarding/${id}/${kind}`);
      toast.success(kind === "complete" ? "Onboarding completed" : "Onboarding cancelled");
      load();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  if (!ob) return <AppShell title="Onboarding"><div className="text-sm text-zinc-500">Loading…</div></AppShell>;

  const stages = {};
  (ob.tasks||[]).forEach(t => { (stages[t.stage] = stages[t.stage] || []).push(t); });
  const stageOrder = ["pre_joining","day_1","week_1","month_1","probation","custom"];
  const done = (ob.tasks||[]).filter(t=>t.status==="done"||t.status==="skipped").length;
  const total = ob.tasks?.length || 0;
  const pct = total === 0 ? 0 : Math.round((done/total)*100);

  return (
    <AppShell title={`Onboarding · ${ob.employee_name}`}>
      <div className="mb-4">
        <Link to="/app/onboarding" className="inline-flex items-center gap-1 text-sm text-zinc-600 hover:text-zinc-950"><ArrowLeft size={14}/> Back</Link>
      </div>
      <div className="bg-white border border-zinc-200 rounded-lg p-6 mb-6" data-testid="onboarding-header">
        <div className="flex items-start justify-between">
          <div>
            <div className="font-display font-bold text-xl">{ob.employee_name}</div>
            <div className="text-sm text-zinc-500 mt-1">Template: {ob.template_name} · DOJ {ob.date_of_joining}</div>
          </div>
          <div className="flex items-center gap-2">
            {ob.status === "active" && <>
              <Button size="sm" variant="outline" onClick={() => finish("complete")} data-testid="ob-mark-complete">Mark complete</Button>
              <Button size="sm" variant="ghost" className="text-red-600" onClick={() => finish("cancel")} data-testid="ob-cancel">Cancel onboarding</Button>
            </>}
            <Badge variant="outline" className="uppercase text-[10px]" data-testid="ob-status">{ob.status}</Badge>
          </div>
        </div>
        <div className="mt-4">
          <div className="flex items-center justify-between text-xs text-zinc-600 mb-1.5"><span>{done} of {total} tasks complete</span><span>{pct}%</span></div>
          <div className="h-2 bg-zinc-100 rounded-full overflow-hidden">
            <div className="h-full bg-emerald-500 transition-all" style={{width:`${pct}%`}}/>
          </div>
        </div>
      </div>

      <div className="space-y-5">
        {stageOrder.filter(s=>stages[s]).map(stage => (
          <SectionCard key={stage} title={STAGE_LABELS[stage]} testid={`stage-${stage}`}>
            <div className="divide-y divide-zinc-100">
              {stages[stage].map(t => (
                <TaskRow
                  key={t.task_id}
                  ob={ob}
                  task={t}
                  onUpdate={updateTask}
                  onActionDone={(updated) => setOb(updated)}
                />
              ))}
            </div>
          </SectionCard>
        ))}
      </div>
    </AppShell>
  );
}


// ----------------- Inline-action task row -----------------
function TaskRow({ ob, task: t, onUpdate, onActionDone }) {
  const [expanded, setExpanded] = useState(false);
  const action = t.action_type || "tick_only";
  const data = t.action_data || {};
  const isDone = t.status === "done" || t.status === "skipped";
  const hasData = data && Object.keys(data).length > 0;

  return (
    <div className="py-3" data-testid={`task-${t.task_id}`}>
      <div className="flex items-center gap-3">
        <button
          onClick={()=>onUpdate(t.task_id, t.status === "done" ? "pending" : "done")}
          className="flex-shrink-0"
          data-testid={`task-check-${t.task_id}`}
          title="Quick mark done"
        >
          {t.status === "done"
            ? <CheckCircle size={22} weight="fill" className="text-emerald-500"/>
            : <Circle size={22} className="text-zinc-300 hover:text-zinc-500"/>
          }
        </button>
        <div className="flex-1 min-w-0">
          <div className={`text-sm font-medium ${t.status==="done"?"line-through text-zinc-400":""}`}>{t.title}</div>
          <div className="text-xs text-zinc-500 mt-0.5 flex items-center gap-2 flex-wrap">
            <span className={`px-1.5 py-0.5 text-[10px] uppercase tracking-wider border rounded ${ASSIGNEE_COLORS[t.assignee]||"bg-zinc-50"}`}>{t.assignee}</span>
            {t.due_date && <span className="inline-flex items-center gap-1"><CalendarBlank size={12}/> {t.due_date}</span>}
            {t.completed_by_name && <span className="text-emerald-600">✓ {t.completed_by_name}</span>}
            <ActionTypePill type={action} />
          </div>
        </div>
        {action !== "tick_only" && (
          <Button
            size="sm"
            variant={isDone ? "outline" : "default"}
            onClick={() => setExpanded(e => !e)}
            data-testid={`task-action-toggle-${t.task_id}`}
          >
            {expanded ? "Close" : isDone ? "View / edit" : (hasData ? "Continue" : actionCTA(action))}
          </Button>
        )}
        <Select value={t.status} onValueChange={(v)=>onUpdate(t.task_id,v)}>
          <SelectTrigger className="h-8 w-32 text-xs" data-testid={`task-status-${t.task_id}`}><SelectValue/></SelectTrigger>
          <SelectContent>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="in_progress">In progress</SelectItem>
            <SelectItem value="done">Done</SelectItem>
            <SelectItem value="skipped">Skipped</SelectItem>
          </SelectContent>
        </Select>
      </div>
      {expanded && action !== "tick_only" && (
        <div className="mt-3 ml-8 bg-zinc-50 border border-zinc-200 rounded-md p-3" data-testid={`task-action-panel-${t.task_id}`}>
          <ActionWidget ob={ob} task={t} onDone={(updated) => { onActionDone(updated); setExpanded(false); }} />
        </div>
      )}
      {!expanded && hasData && (
        <div className="mt-2 ml-8 text-xs text-zinc-600" data-testid={`task-action-summary-${t.task_id}`}>
          <ActionSummary action={action} data={data} />
        </div>
      )}
    </div>
  );
}

function ActionTypePill({ type }) {
  const labels = {
    tick_only: { label: "tick", cls: "bg-zinc-100 text-zinc-600" },
    send_letter: { label: "send letter", cls: "bg-blue-50 text-blue-700" },
    upload_docs: { label: "upload", cls: "bg-violet-50 text-violet-700" },
    record_details: { label: "record", cls: "bg-amber-50 text-amber-700" },
    link_url: { label: "link", cls: "bg-emerald-50 text-emerald-700" },
    schedule_meeting: { label: "schedule", cls: "bg-rose-50 text-rose-700" },
  };
  const m = labels[type] || labels.tick_only;
  return <span className={`px-1.5 py-0.5 text-[10px] uppercase tracking-wider rounded ${m.cls}`}>{m.label}</span>;
}

function actionCTA(action) {
  return ({
    send_letter: "Send letter",
    upload_docs: "Upload",
    record_details: "Record",
    link_url: "Add link",
    schedule_meeting: "Schedule",
  })[action] || "Open";
}

function ActionSummary({ action, data }) {
  if (action === "record_details" && data.values) {
    const entries = Object.entries(data.values).filter(([_, v]) => v);
    if (!entries.length) return null;
    return <div className="flex flex-wrap gap-x-3 gap-y-0.5">{entries.map(([k, v]) => <span key={k}><span className="text-zinc-400">{k}:</span> <span className="text-zinc-700">{String(v).slice(0, 60)}</span></span>)}</div>;
  }
  if (action === "link_url" && data.url) {
    return <a href={data.url} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline inline-flex items-center gap-1"><LinkSimple size={11}/> {data.url}</a>;
  }
  if (action === "schedule_meeting" && data.meeting_at) {
    return <span><ClockClockwise size={11} className="inline -mt-0.5 mr-1"/>Scheduled for {data.meeting_at.slice(0, 16).replace("T", " ")} {data.location ? `· ${data.location}` : ""}</span>;
  }
  if (action === "upload_docs" && data.doc_types_uploaded?.length) {
    return <span><FileText size={11} className="inline -mt-0.5 mr-1"/>Uploaded: {data.doc_types_uploaded.join(", ")}</span>;
  }
  if (action === "send_letter" && data.letter_id) {
    return <Link to={`/app/letters?lid=${data.letter_id}`} className="text-blue-600 hover:underline inline-flex items-center gap-1"><PaperPlaneTilt size={11}/> Sent: {data.template_name || "letter"}</Link>;
  }
  return null;
}


// ----------------- Per-action widgets -----------------
function ActionWidget({ ob, task, onDone }) {
  const action = task.action_type;
  if (action === "send_letter") return <SendLetterWidget ob={ob} task={task} onDone={onDone} />;
  if (action === "upload_docs") return <UploadDocsWidget ob={ob} task={task} onDone={onDone} />;
  if (action === "record_details") return <RecordDetailsWidget ob={ob} task={task} onDone={onDone} />;
  if (action === "link_url") return <LinkUrlWidget ob={ob} task={task} onDone={onDone} />;
  if (action === "schedule_meeting") return <ScheduleMeetingWidget ob={ob} task={task} onDone={onDone} />;
  return null;
}

async function postAction(ob_id, task_id, action_data, mark_done = true) {
  const { data } = await api.post(`/onboarding/${ob_id}/task/${task_id}/action`, {
    action_data, mark_done,
  });
  return data;
}

function HelpHint({ task }) {
  const hint = task.action_config?.hint;
  if (!hint) return null;
  return <div className="text-xs text-zinc-500 italic mb-2">{hint}</div>;
}

function SendLetterWidget({ ob, task, onDone }) {
  const [templates, setTemplates] = useState([]);
  const [templateId, setTemplateId] = useState(task.action_data?.template_id || "");
  const [recipient, setRecipient] = useState(task.action_data?.recipient_email || "");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get("/letter-templates").then(r => setTemplates(r.data)).catch(() => setTemplates([]));
  }, []);
  const submit = async () => {
    if (!templateId) { toast.error("Pick a letter template"); return; }
    setBusy(true);
    try {
      const updated = await postAction(ob.id, task.task_id, { template_id: templateId, recipient_email: recipient || undefined });
      const t = updated.tasks.find(x => x.task_id === task.task_id);
      if (t?.action_data?.email_sent) toast.success("Letter generated & emailed");
      else toast.success("Letter generated (email not sent — check Resend config)");
      onDone(updated);
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Send failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="space-y-2">
      <HelpHint task={task} />
      <Label className="text-xs">Letter template</Label>
      <Select value={templateId} onValueChange={setTemplateId}>
        <SelectTrigger className="h-9 bg-white" data-testid={`send-letter-tpl-${task.task_id}`}><SelectValue placeholder={templates.length ? "Pick a template" : "No letter templates — create one in Letters"}/></SelectTrigger>
        <SelectContent>{templates.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
      </Select>
      <div>
        <Label className="text-xs">Recipient email (defaults to employee's email)</Label>
        <Input value={recipient} onChange={(e) => setRecipient(e.target.value)} placeholder="leave blank to use employee email" className="mt-1 bg-white" data-testid={`send-letter-to-${task.task_id}`}/>
      </div>
      <div className="text-[11px] text-zinc-500">The letter will be addressed to {ob.employee_name} and auto-fills employee_name, designation, DOJ, etc.</div>
      <div className="flex gap-2 pt-1">
        <Button size="sm" onClick={submit} disabled={busy || !templateId} data-testid={`send-letter-submit-${task.task_id}`}>{busy ? "Sending…" : "Send & mark done"}</Button>
      </div>
    </div>
  );
}

function UploadDocsWidget({ ob, task, onDone }) {
  const docTypes = task.action_config?.doc_types || [];
  const [files, setFiles] = useState({}); // doc_type -> {name, b64, mime}
  const [busy, setBusy] = useState(false);
  const inputRefs = useRef({});

  const handleFile = (docType, file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error(`${file.name} exceeds 5MB`); return; }
    const reader = new FileReader();
    reader.onload = (ev) => {
      const b64 = ev.target.result.split(",")[1];
      setFiles(f => ({ ...f, [docType]: { name: file.name, b64, mime: file.type || "application/pdf" } }));
    };
    reader.readAsDataURL(file);
  };

  const submit = async () => {
    const uploads = Object.entries(files).map(([doc_type, v]) => ({
      doc_type, file_name: v.name, file_data_b64: v.b64, mime_type: v.mime,
    }));
    if (!uploads.length) { toast.error("Attach at least one document"); return; }
    setBusy(true);
    try {
      const updated = await postAction(ob.id, task.task_id, { uploads });
      toast.success(`Uploaded ${uploads.length} document(s)`);
      onDone(updated);
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Upload failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-2">
      <HelpHint task={task} />
      <div className="space-y-2">
        {docTypes.map(dt => (
          <div key={dt.key} className="flex items-center gap-2 bg-white border border-zinc-200 rounded px-3 py-2">
            <div className="flex-1">
              <div className="text-sm">{dt.label} {dt.required && <span className="text-rose-500">*</span>}</div>
              {files[dt.key] && <div className="text-[11px] text-emerald-600 truncate">{files[dt.key].name}</div>}
            </div>
            <input
              ref={el => inputRefs.current[dt.key] = el}
              type="file"
              accept=".pdf,.png,.jpg,.jpeg"
              className="hidden"
              onChange={(e) => handleFile(dt.key, e.target.files[0])}
              data-testid={`upload-file-${task.task_id}-${dt.key}`}
            />
            <Button size="sm" variant="outline" onClick={() => inputRefs.current[dt.key]?.click()} className="gap-1">
              <UploadSimple size={12}/> {files[dt.key] ? "Replace" : "Attach"}
            </Button>
          </div>
        ))}
        {!docTypes.length && <div className="text-xs text-zinc-500">No document slots configured for this task.</div>}
      </div>
      <div className="text-[11px] text-zinc-500">Stored under this employee's Joiner Documents (same as the magic-link wizard). 5MB cap per file.</div>
      <div className="flex gap-2 pt-1">
        <Button size="sm" onClick={submit} disabled={busy || !Object.keys(files).length} data-testid={`upload-submit-${task.task_id}`}>
          {busy ? "Uploading…" : "Upload & mark done"}
        </Button>
      </div>
    </div>
  );
}

function RecordDetailsWidget({ ob, task, onDone }) {
  const fields = task.action_config?.fields || [];
  const [values, setValues] = useState(() => ({ ...(task.action_data?.values || {}) }));
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setValues(prev => ({ ...prev, [k]: v }));
  const submit = async () => {
    const cleaned = Object.fromEntries(Object.entries(values).filter(([_, v]) => v != null && String(v).trim() !== ""));
    if (!Object.keys(cleaned).length) { toast.error("Fill at least one field"); return; }
    setBusy(true);
    try {
      const updated = await postAction(ob.id, task.task_id, { values: cleaned });
      toast.success("Saved");
      onDone(updated);
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Save failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="space-y-2">
      <HelpHint task={task} />
      <div className="grid grid-cols-2 gap-2">
        {fields.map(f => (
          <div key={f.key} className={f.type === "textarea" ? "col-span-2" : ""}>
            <Label className="text-xs">{f.label}</Label>
            {f.type === "textarea"
              ? <Textarea rows={2} value={values[f.key] || ""} onChange={(e) => set(f.key, e.target.value)} placeholder={f.placeholder || ""} className="mt-1 bg-white" data-testid={`record-${task.task_id}-${f.key}`}/>
              : <Input type={f.type === "email" ? "email" : "text"} value={values[f.key] || ""} onChange={(e) => set(f.key, e.target.value)} placeholder={f.placeholder || ""} className="mt-1 bg-white" data-testid={`record-${task.task_id}-${f.key}`}/>
            }
          </div>
        ))}
        {!fields.length && <div className="text-xs text-zinc-500 col-span-2">No fields configured for this task.</div>}
      </div>
      <div className="flex gap-2 pt-1">
        <Button size="sm" onClick={submit} disabled={busy} data-testid={`record-submit-${task.task_id}`}>{busy ? "Saving…" : "Save & mark done"}</Button>
      </div>
    </div>
  );
}

function LinkUrlWidget({ ob, task, onDone }) {
  const [url, setUrl] = useState(task.action_data?.url || "");
  const [note, setNote] = useState(task.action_data?.note || "");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!url) { toast.error("Paste a URL"); return; }
    setBusy(true);
    try {
      const updated = await postAction(ob.id, task.task_id, { url, note });
      toast.success("Link saved");
      onDone(updated);
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Save failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="space-y-2">
      <HelpHint task={task} />
      <div>
        <Label className="text-xs">URL</Label>
        <Input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://drive.google.com/…" className="mt-1 bg-white" data-testid={`link-url-${task.task_id}`}/>
      </div>
      <div>
        <Label className="text-xs">Note (optional)</Label>
        <Input value={note} onChange={(e) => setNote(e.target.value)} className="mt-1 bg-white" data-testid={`link-note-${task.task_id}`}/>
      </div>
      <div className="flex gap-2 pt-1">
        <Button size="sm" onClick={submit} disabled={busy} data-testid={`link-submit-${task.task_id}`}>{busy ? "Saving…" : "Save & mark done"}</Button>
      </div>
    </div>
  );
}

function ScheduleMeetingWidget({ ob, task, onDone }) {
  const [meetingAt, setMeetingAt] = useState(task.action_data?.meeting_at?.slice(0, 16) || "");
  const [location, setLocation] = useState(task.action_data?.location || "");
  const [note, setNote] = useState(task.action_data?.note || "");
  const [markDone, setMarkDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!meetingAt) { toast.error("Pick a date & time"); return; }
    setBusy(true);
    try {
      const iso = new Date(meetingAt).toISOString();
      const updated = await postAction(ob.id, task.task_id, { meeting_at: iso, location, note }, markDone);
      toast.success(markDone ? "Saved & marked done" : "Meeting scheduled");
      onDone(updated);
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Save failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="space-y-2">
      <HelpHint task={task} />
      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label className="text-xs">Date & time</Label>
          <Input type="datetime-local" value={meetingAt} onChange={(e) => setMeetingAt(e.target.value)} className="mt-1 bg-white" data-testid={`meet-when-${task.task_id}`}/>
        </div>
        <div>
          <Label className="text-xs">Location / link</Label>
          <Input value={location} onChange={(e) => setLocation(e.target.value)} placeholder="HR Room / Zoom URL" className="mt-1 bg-white" data-testid={`meet-where-${task.task_id}`}/>
        </div>
      </div>
      <div>
        <Label className="text-xs">Note (optional)</Label>
        <Input value={note} onChange={(e) => setNote(e.target.value)} className="mt-1 bg-white" data-testid={`meet-note-${task.task_id}`}/>
      </div>
      <label className="flex items-center gap-2 text-xs text-zinc-600 cursor-pointer">
        <input type="checkbox" checked={markDone} onChange={(e) => setMarkDone(e.target.checked)} data-testid={`meet-markdone-${task.task_id}`}/>
        Also mark task as done (otherwise it stays in progress until the meeting happens)
      </label>
      <div className="flex gap-2 pt-1">
        <Button size="sm" onClick={submit} disabled={busy} data-testid={`meet-submit-${task.task_id}`}>{busy ? "Saving…" : "Save"}</Button>
      </div>
    </div>
  );
}
